import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Bell, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isNativePlatform, isNotificationSupported } from '@/utils/platformDetection';

interface CustomerNotificationPromptProps {
  onClose: () => void;
  onSubscribe: () => void;
}

export function CustomerNotificationPrompt({ onClose, onSubscribe }: CustomerNotificationPromptProps) {
  const { t } = useTranslation();
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('unsupported');
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    if (isNotificationSupported()) {
    setPermission(Notification.permission);
    } else {
      setPermission('unsupported');
    }
  }, []);

  const handleSubscribe = async () => {
    if (!isNotificationSupported()) {
      console.log('Notifications not supported on this platform');
      return;
    }

    setIsRequesting(true);
    try {
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult === 'granted') {
        onSubscribe();
        onClose();
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  // Don't render on native platforms
  if (isNativePlatform()) {
    return null;
  }

  // Don't render if permission is already granted or denied
  if (permission === 'granted' || permission === 'denied') {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 bg-background border border-border rounded-lg shadow-lg p-4 z-50">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <Bell className="h-5 w-5 text-primary" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground">
            {t('notifications.enableNotifications')}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t('notifications.enableNotificationsDescription')}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleSubscribe}
            disabled={isRequesting}
          >
            {isRequesting ? t('notifications.enabling') : t('notifications.enable')}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

 